
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InsertProposal, Proposal, insertProposalSchema } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { PlusIcon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";

const proposalFormSchema = insertProposalSchema.extend({
  title: z.string().min(1, "Title is required"),
  highlights: z.array(z.string()).min(1, "At least one highlight is required"),
});

type ProposalFormData = z.infer<typeof proposalFormSchema>;

interface ProposalFormProps {
  tripId: number;
  initialData?: Proposal | null;
  onSubmit: (data: InsertProposal) => void;
  isSubmitting: boolean;
}

const sanitizeHighlights = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [""];
  }

  const next = value.filter((item): item is string => typeof item === "string");
  return next.length > 0 ? next : [""];
};

export function ProposalForm({ tripId, initialData, onSubmit, isSubmitting }: ProposalFormProps) {
  const { toast } = useToast();
  const initialHighlights = useMemo(() => sanitizeHighlights(initialData?.highlights), [initialData?.highlights]);
  const [highlights, setHighlights] = useState<string[]>(initialHighlights);
  const [highlightInput, setHighlightInput] = useState("");

  const defaultValues: Partial<ProposalFormData> = {
    tripId,
    title: initialData?.title ?? "",
    status: initialData?.status ?? "draft",
    advisorNote: initialData?.advisorNote ?? "",
    termsAndConditions: initialData?.termsAndConditions ?? "",
    coverImageUrl: initialData?.coverImageUrl ?? "",
    highlights: initialHighlights,
    pricing: initialData?.pricing ?? null,
  };

  const form = useForm<ProposalFormData>({
    resolver: zodResolver(proposalFormSchema),
    defaultValues,
  });

  const addHighlight = () => {
    const trimmed = highlightInput.trim();
    if (!trimmed) {
      return;
    }

    const newHighlights = [...highlights, trimmed];
    setHighlights(newHighlights);
    form.setValue("highlights", newHighlights, { shouldDirty: true });
    setHighlightInput("");
  };

  const removeHighlight = (index: number) => {
    const newHighlights = highlights.filter((_, i) => i !== index);
    const nextHighlights = newHighlights.length > 0 ? newHighlights : [""];
    setHighlights(nextHighlights);
    form.setValue("highlights", nextHighlights, { shouldDirty: true });
  };

  const handleSubmit = (data: ProposalFormData) => {
    try {
      onSubmit(data);
    } catch (error) {
      console.error("Error submitting proposal form:", error);
      toast({
        title: "Error",
        description: "Failed to save proposal. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Proposal Title</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter proposal title"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              </FormControl>
              <FormDescription>A descriptive title for the proposal.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="coverImageUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cover Image URL</FormLabel>
              <FormControl>
                <Input
                  placeholder="https://example.com/image.jpg"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              </FormControl>
              <FormDescription>
                URL to an image that will be used as the cover for the proposal.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <Label>Trip Highlights</Label>
          <div className="flex flex-col gap-2">
            {highlights.map((highlight, index) => (
              <div key={`${index}-${highlight}`} className="flex items-center gap-2">
                <Input
                  value={highlight}
                  onChange={(event) => {
                    const newHighlights = [...highlights];
                    newHighlights[index] = event.target.value;
                    setHighlights(newHighlights);
                    form.setValue("highlights", newHighlights, { shouldDirty: true });
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon" aria-label={`Remove highlight ${index + 1}`}
                  onClick={() => removeHighlight(index)}
                  className="h-8 w-8"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Add a highlight"
                value={highlightInput}
                onChange={(event) => setHighlightInput(event.target.value)}
                className="flex-1"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addHighlight();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addHighlight}
                className="h-8"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
          {form.formState.errors.highlights && (
            <p className="text-sm font-medium text-destructive">
              {form.formState.errors.highlights.message}
            </p>
          )}
        </div>

        <FormField
          control={form.control}
          name="advisorNote"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Advisor Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter notes or personal message for the client"
                  className="min-h-[100px]"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              </FormControl>
              <FormDescription>Personal message or notes that will appear in the proposal.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="termsAndConditions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terms and Conditions</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter terms and conditions"
                  className="min-h-[100px]"
                  {...field}
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              </FormControl>
              <FormDescription>Standard terms and conditions for the trip.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <FormControl>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={field.value ?? "draft"}
                  onChange={(event) => field.onChange(event.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </FormControl>
              <FormDescription>Current status of the proposal.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2 justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save Proposal"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
